import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {UserPrivilegeService} from '../../services/common/user-privilege/user-privilege.service';
import {OneTimeConfirmationDialogService} from '../../dialogs/one-time-confirmation-dialog/service/one-time-confirmation-dialog.service';
import {OnlineNodeService} from '../services/online/node/online-node.service';
import {OnlineNetworkService} from '../services/online/network/online-network.service';
import {OnlineVoipPeerService} from '../services/online/voip-peer/online-voip-peer.service';
import {OnlineConnectionService} from '../services/online/connection/online-connection.service';
import {OnlinePeerConnectionService} from '../services/online/peer-connection/online-peer-connection.service';
import {NetworkLayersService} from '../services/map/network-layers/network-layers.service';
import {LicenseFeaturesService} from '../../services/common/license-features/license-features.service';
import {PeerConnectionApiService} from '../../services/api/peer-connection/peer-connection-api.service';
import {DataItemToVisualsMappersService} from '../services/map/data-item-to-visuals-mappers/data-item-to-visuals-mappers.service';
import {DataCacheApiService} from '../services/map/data-cache-api/data-cache-api.service';
import * as _ from 'lodash';
import {MapDiagramComponent} from './map-diagram/map-diagram.component';
import {MapService} from '../services/map/map/map.service';
import {PconsDialogService} from '../pcons/pcons-dialog/pcons-dialog.service';
import {takeWhile} from 'rxjs/operators';
import {timer} from 'rxjs';
import {MapSearchComponent} from './map-search/map-search.component';
import {SearchTermDefinitionInf} from '../../interfaces/common/search/search-term-definition-inf';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnInit, OnDestroy, AfterViewInit {
  constructor(private userPrivilege: UserPrivilegeService,
              private mapService: MapService,
              private oneTimeConfirmationDialogService: OneTimeConfirmationDialogService,
              private nodeService: OnlineNodeService,
              private networkService: OnlineNetworkService,
              private voipPeerService: OnlineVoipPeerService,
              private connectionService: OnlineConnectionService,
              private pConService: OnlinePeerConnectionService,
              private networkLayersService: NetworkLayersService,
              private licenseFeaturesService: LicenseFeaturesService,
              private peerConnectionsApiService: PeerConnectionApiService,
              private dataItemToVisualsMappersService: DataItemToVisualsMappersService,
              private changeDetectorRef: ChangeDetectorRef,
              private pconsDialogService: PconsDialogService,
              private dataCacheApiService: DataCacheApiService) {
  }

  @ViewChild(MapDiagramComponent) mapDiagram;

  mapSearchComponent = MapSearchComponent;
  searchData;
  searchTerms: SearchTermDefinitionInf[] = [
    { term: 'name', operator: ':', displayName: 'Name'},
    { term: 'administrative state', operator: '=', displayName: 'Administrative State' },
    { term: 'operative state', operator: '=', displayName: 'Operative State' },
    { term: 'elements', operator: '=', displayName: 'Elements' }
  ];
  diagramApi: any;
  isAuthorized: boolean;
  isTestRouteDisabled: boolean;
  isQualityEnabled: boolean;
  autoRefreshSubscription;
  dataReady = false;
  diagramSelection;

  refreshObservable;
  removeDraggedSvgObservable;
  drawTestRouteObservable;

  readonly testRouteStoreKey = 'test-route-store-key';

  ngOnInit(): void {
    this.dataCacheApiService.getDataReady().pipe(takeWhile(() => this.dataReady === false)).subscribe(x => {
      if (x === true) {
        this.dataReady = true;
        this.initializeQualityBasedRoutingConfigurations();
      }
    });

    this.setSearchOptions();

    this.isAuthorized = this.userPrivilege.isAuthorized();
    this.registerAutoRefresh();
    this.statisticsAutoRefresh();

    this.licenseFeaturesService.isFeatureSupported('testRoute').subscribe((isFeatureSupported) => {
      this.isTestRouteDisabled = !isFeatureSupported;
    });

    this.refreshObservable = this.mapService.onRefresh().subscribe(() => {
      this.refreshData();
    });

    this.removeDraggedSvgObservable = this.mapService.onRemoveDraggedSvg().subscribe(() => {
      this.mapDiagram.removeDraggedSvg();
    });

    this.drawTestRouteObservable = this.mapService.onDrawTestRoute().subscribe((paths) => {
      if (paths === null) {
        this.clearAllPaths();
        return;
      }
      this.drawPath(paths);
    });
    this.dataCacheApiService.initData();
  }

  registerAutoRefresh(): void {
    const timeInterval = 30 * 1000;
    this.autoRefreshSubscription = timer(timeInterval, timeInterval).subscribe(() => {
      this.refreshData();
    });
  }

  ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges();
  }

  statisticsAutoRefresh(): void {
    const timeInterval = 5 * 60 * 1000;
    timer(timeInterval, timeInterval).subscribe(() => {
      // this.sharedEventService.broadcast('nothingSelectedStatisticsRefresh');
      // this.sharedEventService.broadcast('topFiveRefresh');
    });
  }

  isSingleSelection(): boolean {
    return this.diagramApi && this.diagramApi.isSingleSelection();
  }

  isConnectionsCluster(): boolean {
    return this.diagramApi.getSelection()[0] &&
      this.diagramApi.getSelection()[0].connectionType === 'cluster';
  }

  isAnyElementsOnMap(): boolean {
    return this.diagramApi && this.diagramApi.getData().nodes.length === 0;
  }

  isLockUnlockPConsDisabled(): boolean {
    return (!this.diagramApi || !this.diagramApi.getSelection().every(item => {
      return item.dataItem.adminState === this.diagramApi.getSelection()[0].dataItem.adminState;
    }));
  }

  isDeleteItemDisabled(): boolean {
    if (this.diagramApi && this.diagramApi.isSingleSelection()) {
      if (this.isSinglePeerConnectionSelection()) {
        return this.isDeletePConDisabledFn(this.diagramApi.getConnectionSelection()[0].dataItem);
      }
      if (this.isSingleNodeSelection()) {
        return this.isDeleteNodeDisabledFn(this.diagramApi.getShapeSelection()[0].dataItem);
      }

      if (this.isSingleVoipPeerSelection() && !this.isVoipPeerConnected(this.diagramApi.getShapeSelection()[0].dataItem)) {
        return false;
      }
      if (this.isSingleConnectionSelection()) {
        return false;
      }
    }
    return true;
  }

  syncTopology(): any {
    this.networkService.syncTopology();
  }

  addConnection(): void {
    if (this.isSingleNodeSelection()) {
      if (this.diagramApi.getShapeSelection()[0].dataItem.productType !== 'THIRD_PARTY') {
        this.connectionService.addConnection(this.diagramApi.getShapeSelection()[0].dataItem.id);
      } else {
        this.connectionService.addConnection(null, this.diagramApi.getShapeSelection()[0].dataItem.id);
      }
    } else {
      this.connectionService.addConnection();
    }
  }

  testRoute(): void {
    this.oneTimeConfirmationDialogService.showPopup(this.testRouteStoreKey,
      'Test Route is also available from the peer connection\'s right click action menu', () => {
        const mapSelection = this.diagramApi.getConnectionSelection();
        const selectedPeerConnection = mapSelection.length === 1 &&
        mapSelection[0].connectionType === 'peerConnection' ?
          mapSelection[0].dataItem.id : null;

        this.pConService.testRoute(selectedPeerConnection);
      });
  }

  dragConnectionMode(): void {
    this.oneTimeConfirmationDialogService.showPopup('DragConnectionExplanation',
      'Click and drag the mouse from one element to the other.',
      () => {
        this.diagramApi.addEdgeMode();
      });
  }

  editItem(): void {
    if (this.isSingleNodeSelection()) {
      this.nodeService.editNode(this.diagramApi.getShapeSelection()[0].dataItem);
    } else if (this.isSingleConnectionSelection()) {
      this.connectionService.editConnection(this.diagramApi.getConnectionSelection()[0].dataItem.id);
    } else if (this.isSingleVoipPeerSelection()) {
      this.voipPeerService.editVoipPeer(this.diagramApi.getShapeSelection()[0].dataItem);
    } else if (this.isSinglePeerConnectionSelection()) {
      if (this.diagramApi.getConnectionSelection()[0].dataItem.peerConnectionNodeType === 'Virtual') {
        this.pConService.editVirtualPCon(this.diagramApi.getConnectionSelection()[0].dataItem.id);
      } else {
        this.pConService.editPcon(this.diagramApi.getConnectionSelection()[0].dataItem.id);
      }
    } else if (this.isSingleClusterSelection()) {
      const onSuccess = (cluster) => {
        const clusterLabel = this.dataItemToVisualsMappersService.prepareLabelForDiagram(cluster.name);
        this.diagramApi.updateCluster(cluster.id, cluster.name, clusterLabel);
      };
      this.networkService.editCluster(this.diagramApi.getShapeSelection()[0], onSuccess);
    }
  }

  diagramItemAdded(droppedItem: any): void {
    if (droppedItem.type === 'aCDevice') {
      this.nodeService.addNode(droppedItem);
    } else if (droppedItem.type === 'thirdPartyDevice') {
      this.nodeService.addThirdPartyDevice(droppedItem);
    } else {
      this.voipPeerService.addVoipPeer(droppedItem);
    }
  }

  deleteItem(): void {
    if (this.isSingleNodeSelection()) {
      this.nodeService.deleteNode(this.diagramApi.getShapeSelection()[0].dataItem);
    } else if (this.isSingleConnectionSelection()) {
      this.connectionService.deleteConnection(this.diagramApi.getConnectionSelection()[0].dataItem);
    } else if (this.isSingleVoipPeerSelection()) {
      this.voipPeerService.deleteVoipPeer(this.diagramApi.getShapeSelection()[0].dataItem);
    } else if (this.isSinglePeerConnectionSelection()) {
      this.pConService.deletePcon(this.diagramApi.getConnectionSelection()[0].dataItem);
    }
  }

  lockUnlockNodeItems(): void {
    if (this.isLockNodeDisabledFn(this.diagramApi.getShapeSelection()[0].dataItem)) {
      this.nodeService.unlockNodes(this.diagramApi.getShapeSelection().map(node => node.dataItem.id));
    } else {
      this.nodeService.lockNodes(this.diagramApi.getShapeSelection().map(node => node.dataItem.id));
    }
  }

  refreshMap(): void {
    this.dataCacheApiService.initDataNow().then(() => {
      this.mapDiagram.manualRefresh();
      this.diagramApi.unselectAll();
    });
  }

  lockPCons(): void {
    this.pConService.lockPcons(this.diagramApi.getConnectionSelection().map(item => item.dataItem.id));
  }

  unlockPCons(): void {
    this.pConService.unlockPcons(this.diagramApi.getConnectionSelection().map(item => item.dataItem.id));
  }

  lockPConsForIncoming(): void {
    this.pConService.lockIncomingPCons(this.diagramApi.getConnectionSelection().map(item => item.dataItem.id));
  }

  lockPConsForOutgoing(): void {
    this.pConService.lockOutgoingPCons(this.diagramApi.getConnectionSelection().map(item => item.dataItem.id));
  }

  selectedItemAdminState(): string {
    return this.diagramApi &&
    this.diagramApi.getConnectionSelection() &&
    this.diagramApi.getConnectionSelection()[0] &&
    this.diagramApi.getConnectionSelection()[0].dataItem ?
      this.diagramApi.getConnectionSelection()[0].dataItem.adminState : null;
  }

  isSinglePeerConnectionSelection(): boolean {
    return this.diagramApi && this.diagramApi.isSingleSelection() &&
      this.diagramApi.getConnectionSelection().length === 1 &&
      this.diagramApi.getConnectionSelection()[0].connectionType === 'peerConnection';
  }

  isAtLeastOneVirtualPConSelected(): boolean {
    if (_.isNil(this.diagramApi)) {
      return;
    }

    const connectionSelection = this.diagramApi.getConnectionSelection();
    return connectionSelection && _.isArray(connectionSelection) &&
      connectionSelection.find(pCon => (pCon.dataItem && pCon.dataItem.peerConnectionNodeType === 'Virtual'));
  }

  isOnlyPConsSelected(): boolean {
    if (!this.diagramApi || this.diagramApi.getSelection().length === 0) {
      return false;
    }

    return this.diagramApi.getSelection().every(item => item.connectionType === 'peerConnection');
  }

  createConnection(draggedEdgeObject): void {
    this.networkService.dragEdge(draggedEdgeObject, () => {
      this.diagramApi.removeEdges([draggedEdgeObject.id]);
    });
  }


  lockItem(): void {
    if (this.isOnlyPConsSelected()) {
      this.lockPCons();
    } else {
      this.lockNodeItems();
    }
  }

  unlockItem(): void {
    if (this.isOnlyPConsSelected()) {
      this.unlockPCons();
    } else {
      this.UnlockNodeItems();
    }
  }

  isNodesInSameAdminState(adminState): boolean {
    return (!this.diagramApi || this.diagramApi.getSelection().length === 0 || !this.diagramApi.getSelection().every(item => {
      return item.shapeType === 'node' && item.dataItem.adminState === adminState;
    }));
  }

  isLockDisabled(): boolean {
    if (this.isOnlyPConsSelected()) {
      return this.selectedItemAdminState() === 'LOCKED';
    }

    return this.isNodesInSameAdminState('UNLOCKED');
  }

  isUnlockDisabled(): boolean {
    if (this.isOnlyPConsSelected()) {
      return this.selectedItemAdminState() === 'UNLOCKED';
    }

    return this.isNodesInSameAdminState('LOCKED');
  }

  onSearch(data): void {
    let searchData;

    if (!_.isEmpty(data.searchString)) {
      searchData = data.searchString;
    } else if (!_.isNil(data.filter) && !_.isEmpty(data.filter)) {
      searchData = data.filter;
    } else {
      searchData = '';
    }

    this.mapDiagram.searchRefresh(searchData);
  }

  private refreshData(): void {
    this.dataCacheApiService.initDataNow().then(() => {
      this.mapDiagram.updateDiagramData();
      this.mapService.refreshSummary();
    });

  }

  private isSingleNodeSelection(): boolean {
    return this.diagramApi && this.diagramApi.isSingleSelection() &&
      this.diagramApi.getShapeSelection().length === 1 &&
      this.diagramApi.getShapeSelection()[0].shapeType === 'node';
  }

  private isSingleConnectionSelection(): boolean {
    return this.diagramApi && this.diagramApi.isSingleSelection() &&
      this.diagramApi.getConnectionSelection().length === 1 &&
      this.diagramApi.getConnectionSelection()[0].connectionType === 'connection';
  }

  private isSingleVoipPeerSelection(): boolean {
    return this.diagramApi && this.diagramApi.isSingleSelection() &&
      this.diagramApi.getShapeSelection().length === 1 &&
      this.diagramApi.getShapeSelection()[0].shapeType === 'voipPeer';
  }

  private isSingleClusterSelection(): boolean {
    return this.diagramApi && this.diagramApi.isSingleSelection() &&
      this.diagramApi.getShapeSelection().length === 1 &&
      this.diagramApi.getShapeSelection()[0].shapeType === 'cluster';
  }

  private isVoipPeerConnected(dataItem): boolean {
    const pConsOfVoipPeer = this.diagramApi.getData().edgesArray.filter((edge) => {
      return edge.connectionType === 'peerConnection' && edge.dataItem.voipPeerId === dataItem.id;
    });
    return pConsOfVoipPeer.length > 0;
  }

  private isLockNodeDisabledFn(dataItem): boolean {
    return dataItem.adminState.toLowerCase() === 'locked';
  }

  private isDeletePConDisabledFn(dataItem): boolean {
    if (dataItem.peerConnectionNodeType === 'Virtual') {
      return false;
    } else {
      return dataItem.operState.toLowerCase() !== 'predeleted';
    }
  }

  private isDeleteNodeDisabledFn(dataItem): boolean {
    return dataItem.adminState.toLowerCase() === 'unlocked';
  }

  private drawPath(paths): void {
    if (!this.diagramApi) {
      return;
    }

    if (paths !== null && Array.isArray(paths) && paths.length > 0) {
      this.diagramApi.drawPath(paths);
    } else {
      this.diagramApi.clearPath();
    }
  }

  private clearAllPaths(): void {
    if (this.diagramApi) {
      this.diagramApi.clearPath();
    }
  }

  private initializeQualityBasedRoutingConfigurations(): void {
    this.isQualityEnabled = this.dataCacheApiService.getQualityBasedRouting().isQualityEnabled;
  }

  private lockNodeItems(): void {
    this.nodeService.lockNodes(this.diagramApi.getShapeSelection().map(node => node.dataItem.id));
  }

  private UnlockNodeItems(): void {
    this.nodeService.unlockNodes(this.diagramApi.getShapeSelection().map(node => node.dataItem.id));
  }

  onDiagramSelect($event: any): void {
    this.diagramSelection = $event;
  }

  private setSearchOptions(): void {
    const adminStateOptions = [
      {text: 'LOCKED', value: 'LOCKED'},
      {text: 'LOCKED INCOMING', value: 'LOCKED_INCOMING'},
      {text: 'LOCKED OUTGOING', value: 'LOCKED_OUTGOING'},
      {text: 'UNLOCKED', value: 'UNLOCKED'}
    ];

    const operStateOptions = [
      {text: 'Available', value: 'Available'},
      {text: 'Unavailable', value: 'Unavailable'},
      {text: 'Predeleted', value: 'Predeleted'},
      {text: 'Unknown', value: 'Unknown'},
      {text: 'Unroutable', value: 'Unroutable'}
    ];

    const elementsOptions = [
      {text: 'Nodes', value: 'Nodes'},
      {text: 'Voip Peers', value: 'Voip Peers'},
      {text: 'Connections', value: 'Connections'},
      {text: 'Peer Connections', value: 'Peer Connections'}
    ];

    this.searchData = {
      adminStateOptions,
      operStateOptions,
      elementsOptions
    };
  }

  ngOnDestroy(): void {
    this.autoRefreshSubscription.unsubscribe();
    this.refreshObservable.unsubscribe();
    this.removeDraggedSvgObservable.unsubscribe();
    this.drawTestRouteObservable.unsubscribe();
    this.dataCacheApiService.setDataReady(false);
  }
}
